import { FormEvent, useCallback, useMemo, useState } from "react"
import {
  Block,
  ButtonIcon,
  ChevronRight,
  LabelMedium,
  ListItem,
  ParagraphMedium,
  SwitchVertical02,
  useStyletron,
} from "@mezo-org/mezo-clay"
import { Form, FormikProps, withFormik } from "formik"

import {
  RootState,
  selectToken,
  selectTokenDecimals,
  useDappSelector,
} from "#/redux-state"
import {
  useSegmentButtonClickedEvent,
  useSwapRate,
  useSwapReserves,
  useSwapSettings,
} from "#/shared/hooks"
import {
  AnalyticsId,
  SwapFormField,
  SwapFormValue,
  SwapFromFieldData,
  SwappableToken,
  SwapSettingsView,
} from "#/shared/types"
import {
  bigIntToFloat,
  floatToBigInt,
  validateMaximumTokenAmount,
} from "#/shared/utils"

import SwapModalAssetSelector from "./SwapModalAssetSelector"
import SwapModalFormSubmitButton from "./SwapModalFormSubmitButton"
import SwapModalInput from "./SwapModalInput"
import SwapModalRate from "./SwapModalRate"
import SwapModalSlippage from "./SwapModalSlippage"

type SwapModalFormProps = {
  values: SwapFormValue
  onSubmitForm: (values: SwapFormValue) => void
}

enum SwapFormError {
  POOL_EMPTY = "Swap pool is empty. Choose different assets to swap.",
  NOT_ENOUGH_LIQUIDITY = "Pool does not have enough liquidity to process your transaction.",
}

function SwapModalFormBase({
  handleSubmit,
  getFieldMeta,
  getFieldHelpers,
}: FormikProps<SwapFormValue>) {
  const [currentField, setCurrentField] = useState<SwapFormField | null>(null)

  const [currentSelector, setCurrentSelector] = useState<SwapFormField | null>(
    null,
  )

  const [isCalculatingSwapAmounts, setIsCalculatingSwapAmounts] =
    useState(false)

  const { value: tokenIn } = getFieldMeta<SwapFromFieldData>("tokenIn")
  const { value: tokenOut } = getFieldMeta<SwapFromFieldData>("tokenOut")

  const { setValue: setTokenInField } =
    getFieldHelpers<SwapFromFieldData>("tokenIn")

  const { setValue: setTokenOutField } =
    getFieldHelpers<SwapFromFieldData>("tokenOut")

  const { setValue: setSlippage } = getFieldHelpers<string>("slippage")

  const { tokenBalance: tokenInBalance, tokenDecimals: tokenInDecimals } =
    useDappSelector((state: RootState) => selectToken(state, tokenIn.token))

  const tokenOutDecimals = useDappSelector((state: RootState) =>
    selectTokenDecimals(state, tokenOut.token),
  )

  const isLowerOrEqualSellTokenBalance = validateMaximumTokenAmount(
    floatToBigInt(tokenIn.amount, tokenInDecimals),
    tokenInBalance,
  )

  const [css, theme] = useStyletron()

  const { emitButtonClickedSegmentEvent } = useSegmentButtonClickedEvent()

  const { getSwapAmountOut } = useSwapRate(tokenOut.token, tokenIn.token)

  const { isSwapPoolEmpty, hasPoolEnoughLiquidity } = useSwapReserves(
    tokenIn.token,
    tokenOut.token,
  )

  const {
    currentSettingsMenu,
    openSlippageSettings,
    openAssetSelector,
    closeSettingsMenu,
  } = useSwapSettings(setCurrentSelector)

  const swapFormError = useMemo(() => {
    if (isSwapPoolEmpty) return SwapFormError.POOL_EMPTY

    const tokenInAmount = floatToBigInt(tokenIn.amount, tokenInDecimals)
    const tokenOutAmount = floatToBigInt(tokenOut.amount, tokenOutDecimals)

    const enoughLiquidity = hasPoolEnoughLiquidity(
      tokenInAmount,
      tokenOutAmount,
    )

    return enoughLiquidity ? null : SwapFormError.NOT_ENOUGH_LIQUIDITY
  }, [
    hasPoolEnoughLiquidity,
    isSwapPoolEmpty,
    tokenIn.amount,
    tokenOut.amount,
    tokenInDecimals,
    tokenOutDecimals,
  ])

  const isValid = useMemo(
    () =>
      Number(tokenIn.amount) > 0 &&
      Number(tokenOut.amount) > 0 &&
      isLowerOrEqualSellTokenBalance &&
      !swapFormError,
    [
      tokenIn.amount,
      tokenOut.amount,
      isLowerOrEqualSellTokenBalance,
      swapFormError,
    ],
  )

  const isInputDisabled = useMemo(
    () =>
      swapFormError === SwapFormError.POOL_EMPTY || isCalculatingSwapAmounts,
    [swapFormError, isCalculatingSwapAmounts],
  )

  const handleSwitchTokens = useCallback(async () => {
    const tokenInNewValue = tokenOut.amount

    const [, amountOut] = await getSwapAmountOut(
      floatToBigInt(tokenInNewValue, tokenOutDecimals),
    )

    const tokenOutNewValue = bigIntToFloat(
      amountOut,
      tokenInDecimals,
      tokenInDecimals,
    )

    await setTokenInField({
      amount: tokenInNewValue === "0" ? "" : tokenInNewValue,
      token: tokenOut.token,
    })
    await setTokenOutField({
      amount: tokenOutNewValue === "0" ? "" : tokenOutNewValue,
      token: tokenIn.token,
    })
  }, [
    tokenIn,
    tokenOut,
    setTokenInField,
    setTokenOutField,
    getSwapAmountOut,
    tokenInDecimals,
    tokenOutDecimals,
  ])

  // NOTE: This will currently work as we only have direct swaps (through one pool).
  // If we decide to introduce indirect swaps (through multiple pools) this would have
  // to be adjusted
  const updateSelectedToken = useCallback(
    (token: SwappableToken) => {
      if (!currentSelector) return

      const isInField = currentSelector === SwapFormField.TOKEN_IN

      const previousToken =
        currentSelector === SwapFormField.TOKEN_IN
          ? tokenIn.token
          : tokenOut.token

      // No need to update if the same asset is chosen
      if (previousToken === token) {
        setCurrentSelector(null)
        return
      }

      let newTokenIn: SwappableToken
      let newTokenOut: SwappableToken

      if (token === "MUSD") {
        newTokenIn = isInField ? "MUSD" : previousToken
        newTokenOut = isInField ? previousToken : "MUSD"
      } else {
        newTokenIn = isInField ? token : "MUSD"
        newTokenOut = isInField ? "MUSD" : token
      }

      // Set new token values and clear inputs
      setTokenInField({ amount: "", token: newTokenIn })
      setTokenOutField({ amount: "", token: newTokenOut })
      setCurrentSelector(null)
    },
    [currentSelector, setTokenInField, setTokenOutField, tokenOut, tokenIn],
  )

  const submitFormHandler = useCallback(
    (e: FormEvent<HTMLFormElement>) => {
      emitButtonClickedSegmentEvent(AnalyticsId.SWAP_REVIEW)
      handleSubmit(e)
    },
    [handleSubmit, emitButtonClickedSegmentEvent],
  )

  return (
    <Form onSubmit={submitFormHandler}>
      <Block
        className={css({
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          gap: theme.sizing.scale200,
        })}
      >
        <SwapModalInput
          label="From"
          fieldName={SwapFormField.TOKEN_IN}
          currentField={currentField}
          setCurrentField={setCurrentField}
          onOpenAssetSelector={() => {
            openAssetSelector(SwapFormField.TOKEN_IN)
          }}
          setIsCalculatingSwapAmounts={setIsCalculatingSwapAmounts}
          disabled={isInputDisabled}
        />
        <ButtonIcon type="button" size="small" onClick={handleSwitchTokens}>
          <SwitchVertical02 color={theme.colors.black} />
        </ButtonIcon>
        <SwapModalInput
          label="To"
          fieldName={SwapFormField.TOKEN_OUT}
          currentField={currentField}
          setCurrentField={setCurrentField}
          onOpenAssetSelector={() => {
            openAssetSelector(SwapFormField.TOKEN_OUT)
          }}
          setIsCalculatingSwapAmounts={setIsCalculatingSwapAmounts}
          disabled={isInputDisabled}
        />
        {swapFormError ? (
          <Block width="100%" margin={`${theme.sizing.scale500} 0`}>
            <ParagraphMedium color={theme.colors.contentNegative}>
              {swapFormError}
            </ParagraphMedium>
          </Block>
        ) : (
          <SwapModalRate
            amountIn={tokenIn.amount}
            amountOut={tokenOut.amount}
            tokenIn={tokenIn.token}
            tokenOut={tokenOut.token}
            isFieldActive={!!currentField}
          />
        )}
        <ListItem
          endEnhancer={() => (
            <Block as="button" onClick={openSlippageSettings} width="100%">
              <ChevronRight color={theme.colors.contentSecondary} />
            </Block>
          )}
          overrides={{
            Content: {
              style: () => ({ margin: 0, minHeight: "42px", border: "none" }),
            },
          }}
        >
          <LabelMedium>Slippage</LabelMedium>
        </ListItem>
        <SwapModalFormSubmitButton
          disabled={!isValid || !!currentField}
          isLoadingState={isCalculatingSwapAmounts}
        />
      </Block>
      <SwapModalSlippage
        isOpen={currentSettingsMenu === SwapSettingsView.SLIPPAGE}
        setSlippage={setSlippage}
        onClose={closeSettingsMenu}
      />
      <SwapModalAssetSelector
        isOpen={currentSettingsMenu === SwapSettingsView.ASSET_SELECTOR}
        currentSelector={currentSelector}
        onTokenSelect={updateSelectedToken}
        onClose={closeSettingsMenu}
        tokenIn={tokenIn.token}
        tokenOut={tokenOut.token}
      />
    </Form>
  )
}

const SwapModalForm = withFormik<SwapModalFormProps, SwapFormValue>({
  mapPropsToValues: ({ values }) => values,
  handleSubmit: (values, { props }) => props.onSubmitForm(values),
  // Validation is done at the asset input level
  validateOnBlur: false,
  validateOnChange: false,
  validateOnMount: false,
})(SwapModalFormBase)

export default SwapModalForm
